import {
  AutomatedTaskType,
  DEFAULT_AUTOMATED_TASK_TYPES,
  MACHINE_TYPES,
  Machine,
  MachineCollectionSummary,
  MachineSale,
  SalesComparisonPeriod,
} from "../../types";
import { DEFAULT_CANCELLED_AUTOMATION_TASK_POLICY } from "../../automation";
import { normalizePhotoAssets } from "../assets";
import {
  normalizeCommissionPayload,
  normalizeCommissionResponse,
} from "../location/normalize";
import { centsFromApi } from "../../currency";

export const normalizeSalesComparisonPeriods = (value: unknown): SalesComparisonPeriod[] => {
  if (!Array.isArray(value)) return [];
  const periods: SalesComparisonPeriod[] = [];
  for (const entry of value) {
    // A partial set of periods would misdescribe the aggregate. Reject the
    // metadata together if any window is malformed.
    if (!entry || typeof entry !== "object") return [];
    const { start, end, timezone } = entry as Record<string, unknown>;
    if (typeof start !== "string" || typeof end !== "string" || typeof timezone !== "string" || !timezone) return [];
    if (![start, end].every((timestamp) => /[zZ]$|[+-]\d{2}:?\d{2}$/.test(timestamp))) return [];
    const startTime = Date.parse(start);
    const endTime = Date.parse(end);
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime < startTime) return [];
    periods.push({ start, end, timezone });
  }
  return periods;
};

const normalizeAutomatedTaskTypes = (value: any): AutomatedTaskType[] => {
  const raw = Array.isArray(value) ? value : DEFAULT_AUTOMATED_TASK_TYPES;
  const normalized = raw.filter((entry): entry is AutomatedTaskType =>
    DEFAULT_AUTOMATED_TASK_TYPES.includes(entry)
  );
  return normalized.length ? normalized : [...DEFAULT_AUTOMATED_TASK_TYPES];
};

const normalizeBackendDateTime = (value: any): string | null => {
  if (!value) return null;
  const text = String(value);
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(text)) return text;
  return `${text}Z`;
};

export const normalizeMachineResponse = (m: any): Machine => {
  const normalizedType =
    MACHINE_TYPES.find(
      (t) =>
        t.toLowerCase() ===
        String(m.type || "")
          .trim()
          .toLowerCase()
    ) || m.type;

  return {
    ...m,
    name: m.name,
    updatedAt: m.updatedAt ?? m.updated_at ?? undefined,
    type: normalizedType,
    active: Boolean(m.active),
    activatedAt: m.activatedAt ?? m.activated_at ?? null,
    deactivatedAt: m.deactivatedAt ?? m.deactivated_at ?? null,
    location: m.location ?? null,
    assigned:
      typeof m.assigned === "boolean"
        ? m.assigned
        : Boolean(m.location ?? m.assigned),
    makeModel: m.makeModel ?? m.make_model ?? null,
    cardReaderDeviceId: m.cardReaderDeviceId ?? m.card_reader_device_id ?? null,
    cardReaderMachineId: m.cardReaderMachineId ?? m.card_reader_machine_id ?? null,
    cardReaderManufacturer:
      m.cardReaderManufacturer ?? m.card_reader_manufacturer ?? null,
    revenueThreshold: m.revenue_threshold ?? m.revenueThreshold ?? null,
    cashThreshold: m.cash_threshold ?? m.cashThreshold ?? null,
    cardThreshold: m.card_threshold ?? m.cardThreshold ?? null,
    commissionThreshold: m?.commission_threshold ?? m?.commissionThreshold,
    disabledThresholds: m?.disabled_thresholds ?? m?.disabledThresholds ?? [],
    cardTransactionSurchargeCents: centsFromApi(
      m.card_transaction_surcharge_cents ?? m.cardTransactionSurchargeCents,
    ),
    collectionInputMode:
      m.collection_input_mode === "credits" || m.collectionInputMode === "credits"
        ? "credits"
        : "dollars",
    collectionWindowMode:
      m.collection_window_mode === "exact_collection_time" ||
      m.collectionWindowMode === "exact_collection_time"
        ? "exact_collection_time"
        : "scheduled_day_boundary",
    creditsPerDollar: Number(m.credits_per_dollar ?? m.creditsPerDollar ?? 4) || 4,
    automatedTaskTypes: normalizeAutomatedTaskTypes(
      m.automated_task_types ?? m.automatedTaskTypes
    ),
    cancelledAutomationTaskPolicy:
      m.cancelled_automation_task_policy ??
      m.cancelledAutomationTaskPolicy ??
      DEFAULT_CANCELLED_AUTOMATION_TASK_POLICY,
    commissionOverrideEnabled: Boolean(
      m.commission_override_enabled ?? m.commissionOverrideEnabled
    ),
    commission: normalizeCommissionResponse(m.commission),
    assets: normalizePhotoAssets(m.assets),
    lastServiceAt: m.lastServiceAt ?? m.last_service_at ?? null,
    daysSinceLastService:
      m.daysSinceLastService ?? m.days_since_last_service ?? null,
    nextVisit: m.nextVisit ?? m.next_visit ?? null,
    card: Boolean(m.card),
    cash: Boolean(m.cash),
    coin: Boolean(m.coin),
  };
};

export const buildMachinePayload = (payload: Machine) => {
  const location =
    payload.location === undefined
      ? undefined // don’t include the field
      : typeof payload.location === "string"
      ? payload.location || null
      : payload.location?.id || null;

  const normalizedType =
    MACHINE_TYPES.find(
      (t) => t.toLowerCase() === (payload.type || "").trim().toLowerCase()
    ) ||
    (payload.type || "").trim() ||
    "Amusement";

  const assigned =
    payload.location === undefined ? undefined : Boolean(location);

  return {
    name: payload.name,
    type: normalizedType,
    active: payload.active,
    make_model: payload.makeModel ?? null,
    ...(assigned === undefined ? {} : { assigned }),
    location,
    card: payload.card ?? false,
    cash: payload.cash ?? false,
    coin: payload.coin ?? false,
    card_reader_device_id: payload.cardReaderDeviceId ?? null,
    card_reader_machine_id: payload.cardReaderMachineId ?? null,
    card_reader_manufacturer: payload.cardReaderManufacturer ?? null,
    card_transaction_surcharge_cents:
      payload.cardTransactionSurchargeCents !== undefined &&
      payload.cardTransactionSurchargeCents !== null
        ? centsFromApi(payload.cardTransactionSurchargeCents)
        : 0,
    collection_input_mode:
      payload.collectionInputMode === "credits" ? "credits" : "dollars",
    collection_window_mode:
      payload.collectionWindowMode === "exact_collection_time"
        ? "exact_collection_time"
        : "scheduled_day_boundary",
    credits_per_dollar:
      payload.creditsPerDollar !== undefined && payload.creditsPerDollar !== null
        ? Math.max(0.01, Number(payload.creditsPerDollar) || 4)
        : 4,
    ...(payload.revenueThreshold !== undefined ? { revenue_threshold: payload.revenueThreshold } : {}),
    ...(payload.cashThreshold !== undefined ? { cash_threshold: payload.cashThreshold } : {}),
    ...(payload.cardThreshold !== undefined ? { card_threshold: payload.cardThreshold } : {}),
    ...(payload.commissionThreshold !== undefined ? { commission_threshold: payload.commissionThreshold } : {}),
    ...(payload.disabledThresholds !== undefined ? { disabled_thresholds: payload.disabledThresholds } : {}),
    automated_task_types:
      payload.automatedTaskTypes ?? DEFAULT_AUTOMATED_TASK_TYPES,
    cancelled_automation_task_policy:
      payload.cancelledAutomationTaskPolicy ??
      DEFAULT_CANCELLED_AUTOMATION_TASK_POLICY,
    ...(payload.updateOutstandingAutomationTasks
      ? { update_outstanding_automation_tasks: true }
      : {}),
    commission_override_enabled: Boolean(payload.commissionOverrideEnabled),
    commission: payload.commissionOverrideEnabled
      ? normalizeCommissionPayload(payload.commission ?? undefined)
      : null,
    notes: payload.notes ?? null,
    id: payload.id,
  } as any;
};

export const normalizeMachineSaleResponse = (sale: any): MachineSale => {
  return {
    ...sale,
    id: sale.id,
    transactionId: sale.transaction_id ?? sale.transactionId,
    machine: sale.machine,
    location: sale.location ?? null,
    timezone: sale.timezone ?? null,
    localDate: sale.local_date ?? sale.localDate ?? null,
    paymentMethod: sale.payment_method ?? sale.paymentMethod,
    cardNumber: sale.card_number ?? sale.cardNumber ?? null,
    cashSalesCents: centsFromApi(sale.cash_sales_cents ?? sale.cashSalesCents),
    cardSalesCents: centsFromApi(sale.card_sales_cents ?? sale.cardSalesCents),
    coinSalesCents: centsFromApi(sale.coin_sales_cents ?? sale.coinSalesCents),
    commissionCents:
      (sale.commission_cents ?? sale.commissionCents) == null
        ? null
        : centsFromApi(sale.commission_cents ?? sale.commissionCents),
    afterCommissionCents:
      (sale.after_commission_cents ?? sale.afterCommissionCents) == null
        ? null
        : centsFromApi(sale.after_commission_cents ?? sale.afterCommissionCents),
    commissionStatus: sale.commission_status ?? sale.commissionStatus ?? "pending",
    commissionSource: sale.commission_source ?? sale.commissionSource ?? "none",
    expenseCardSurchargeCents:
      sale.expense_card_surcharge_cents != null ||
      sale.expenseCardSurchargeCents != null
        ? centsFromApi(
            sale.expense_card_surcharge_cents ??
              sale.expenseCardSurchargeCents
          )
        : undefined,
    expenseCardFeeBaseCents:
      sale.expense_card_fee_base_cents != null ||
      sale.expenseCardFeeBaseCents != null
        ? centsFromApi(
            sale.expense_card_fee_base_cents ?? sale.expenseCardFeeBaseCents
          )
        : undefined,
    machineNameSnapshot:
      sale.machine_name_snapshot ?? sale.machineNameSnapshot ?? undefined,
    locationNameSnapshot:
      sale.location_name_snapshot ?? sale.locationNameSnapshot ?? undefined,
    isVoided: Boolean(sale.is_voided ?? sale.isVoided),
    voidReason: sale.void_reason ?? sale.voidReason ?? null,
    voidedAt: normalizeBackendDateTime(
      sale.voided_at ?? sale.voidedAt
    ),
    timestamp: normalizeBackendDateTime(sale.timestamp) || sale.timestamp,
  };
};

export const normalizeMachineCollectionSummaryResponse = (
  summary: any
): MachineCollectionSummary => ({
  machine: String(summary?.machine ?? ""),
  window: summary?.window
    ? {
        start: normalizeBackendDateTime(summary.window.start),
        end: normalizeBackendDateTime(summary.window.end) || "",
        basis:
          summary.window.basis === "exact_collection_time"
            ? "exact_collection_time"
            : "scheduled_day_boundary",
        previousCollectionId:
          summary.window.previousCollectionId ??
          summary.window.previous_collection_id ??
          null,
        previousCollectionDoneAt:
          normalizeBackendDateTime(
            summary.window.previousCollectionDoneAt ??
              summary.window.previous_collection_done_at
          ),
        previousCollectionScheduledFor:
          summary.window.previousCollectionScheduledFor ??
          summary.window.previous_collection_scheduled_for ??
          null,
      }
    : null,
  cashSalesCents: centsFromApi(summary?.cashSalesCents ?? summary?.cash_sales_cents),
  cardSalesCents: centsFromApi(summary?.cardSalesCents ?? summary?.card_sales_cents),
  coinSalesCents: centsFromApi(summary?.coinSalesCents ?? summary?.coin_sales_cents),
  grossCents: centsFromApi(summary?.grossCents ?? summary?.gross_cents),
  transactionCount:
    Number(summary?.transactionCount ?? summary?.transaction_count ?? 0) || 0,
  cashTransactionCount: summary?.cashTransactionCount ?? summary?.cash_transaction_count,
  cardTransactionCount:
    Number(summary?.cardTransactionCount ?? summary?.card_transaction_count ?? 0) || 0,
  cardTransactionSurchargeTotalCents:
    centsFromApi(
      summary?.cardTransactionSurchargeTotalCents ??
        summary?.card_transaction_surcharge_total_cents ??
        0
    ),
  grossAfterCardTransactionSurchargeCents:
    centsFromApi(
      summary?.grossAfterCardTransactionSurchargeCents ??
        summary?.gross_after_card_transaction_surcharge_cents ??
        0
    ),
  commissionCents:
    summary?.commissionCents != null || summary?.commission_cents != null
      ? centsFromApi(summary?.commissionCents ?? summary?.commission_cents)
      : null,
  afterCommissionCents:
    summary?.afterCommissionCents != null || summary?.after_commission_cents != null
      ? centsFromApi(
          summary?.afterCommissionCents ?? summary?.after_commission_cents,
        )
      : null,
  commissionStatus:
    summary?.commissionStatus ?? summary?.commission_status ?? "pending",
  previousGrossCents:
    centsFromApi(summary?.previousGrossCents ?? summary?.previous_gross_cents),
  days: summary?.days ?? null,
  previousDays: summary?.previousDays ?? summary?.previous_days ?? null,
});
